import React, { useEffect } from 'react';
import { WidgetWrapper } from '../../../../components/WidgetWrapper/WidgetWrapper';
import { EditField } from '../../../../components/EditPanel/types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import { themeColor } from '../../../../types/color';
import { ConciergeHomeSubtitleState } from './types';

/**
 * Load a Google Font stylesheet into document.head.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: ConciergeHomeSubtitleState = {
  textTemplate: "I'm {concierge_name}, your shopping assistant for {brand}.",
  fontSize: 24,
  fontWeight: 'semibold',
  fontFamily: 'inherit',
  color: themeColor('textPrimary'),
  lineHeight: 1.2,
  letterSpacing: 0.08,
  maxWidth: 830,
  marginTop: 0,
  marginBottom: 0,
  mobile_fontSize: 18,
};

const editFields: EditField[] = [
  {
    key: 'fontSize',
    label: 'Font Size',
    type: 'number',
    min: 14,
    max: 32,
    step: 1,
    responsive: true,
  },
  {
    key: 'fontWeight',
    label: 'Font Weight',
    type: 'options',
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Medium', value: 'medium' },
      { label: 'Semibold', value: 'semibold' },
      { label: 'Bold', value: 'bold' },
    ],
  },
  {
    key: 'fontFamily',
    label: 'Font Family',
    type: 'font',
  },
  { key: 'color', label: 'Color', type: 'color' },
  {
    key: 'lineHeight',
    label: 'Line Height',
    type: 'number',
    min: 1,
    max: 2,
    step: 0.1,
  },
  {
    key: 'letterSpacing',
    label: 'Letter Spacing',
    type: 'number',
    min: -1,
    max: 2,
    step: 0.01,
  },
  {
    key: 'maxWidth',
    label: 'Max Width',
    type: 'number',
    min: 200,
    max: 900,
    step: 10,
  },
  {
    key: 'marginTop',
    label: 'Spacing Above',
    type: 'number',
    min: 0,
    max: 48,
    step: 4,
  },
  {
    key: 'marginBottom',
    label: 'Spacing Below',
    type: 'number',
    min: 0,
    max: 48,
    step: 4,
  },
];

interface ConciergeHomeSubtitleWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeSubtitleState>;
  brandName?: string;
  conciergeName?: string;
}

export const ConciergeHomeSubtitleWidget: React.FC<ConciergeHomeSubtitleWidgetProps> = ({
  widgetId = 'concierge-home-subtitle',
  displayName = 'Subtitle',
  initialState = {},
  brandName = '',
  conciergeName = 'Concierge',
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHomeSubtitleState>(widgetId, { ...defaultState, ...initialState }, editFields);

  // Resolve font family
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== 'inherit'
      ? themeFonts[fontKey]
      : themeFonts['studio-brand-font'] ?? Object.values(themeFonts)[0] ?? null;

  const googleFontLink = fontData && fontData.format === 'google' ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === 'system'
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : 'inherit';

  const needsFontFace = fontData && fontData.format !== 'system' && fontData.format !== 'google';

  const uniqueClass = `subtitle-${widgetId || 'default'}`;

  const mobileFontSize = (state as any).mobile_fontSize ?? state.fontSize;

  const fontStyleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ''
    }
    ${fontFamilyCss !== 'inherit' ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }` : ''}
    @media (max-width: 767px) {
      .${uniqueClass}-wrapper {
        padding-left: 16px !important;
        padding-right: 16px !important;
        box-sizing: border-box !important;
      }
      .${uniqueClass}-text {
        font-size: ${mobileFontSize}px !important;
        line-height: 28px !important;
        letter-spacing: 0.08px !important;
      }
    }
  `.trim();

  // Resolve template tokens
  // For Brandclub, use "your Brandclub shopping assistant" instead of "your shopping assistant for Brandclub"
  const isBrandclub = !brandName || brandName.toLowerCase() === 'brandclub';
  const resolvedText = isBrandclub
    ? `I'm ${conciergeName}, your Brandclub shopping assistant.`
    : state.textTemplate.replace('{concierge_name}', conciergeName).replace('{brand}', brandName);
  const subtitleText = resolvedText;

  const fontWeight =
    state.fontWeight === 'normal'
      ? 400
      : state.fontWeight === 'medium'
        ? 500
        : state.fontWeight === 'semibold'
          ? 600
          : 700;

  const content = (
    <>
      <style>{fontStyleCss}</style>
      <div
        className={`${uniqueClass}-wrapper${fontFamilyCss !== 'inherit' ? ` ${uniqueClass}` : ''}`}
        style={{
          textAlign: 'center',
          marginTop: state.marginTop,
          marginBottom: state.marginBottom,
          width: '100%',
        }}
      >
        <p
          className={`${uniqueClass}-text`}
          style={{
            fontSize: state.fontSize,
            fontWeight,
            color: resolveColor(state.color),
            lineHeight: state.lineHeight,
            letterSpacing: state.letterSpacing ? `${state.letterSpacing}px` : undefined,
            maxWidth: state.maxWidth,
            margin: '0 auto',
          }}
        >
          {subtitleText}
        </p>
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {content}
    </WidgetWrapper>
  );
};
